import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { usePermission, useAuthStore } from '../store/auth';
import { cn } from '../ui';

// Customers (retailer/farmer logins) can't open the staff-facing detail pages — those routes
// denyRoles them — so a link would only 403. Mirror App.tsx's CUSTOMER_ROLES here.
const CUSTOMER_ROLES = ['RETAILER', 'FARMER'];

const linkCls = 'text-brand-700 underline-offset-2 transition-colors hover:underline dark:text-brand-400';

/**
 * Retailer name → its detail page (`/retailers/:id`), but ONLY when the viewer can actually open it
 * (holds RETAILER.VIEW and isn't a customer — that route denies customers). Otherwise renders the
 * children as plain text, so it degrades safely for a retailer looking at their own order.
 */
export function RetailerLink({ id, className, children }: { id?: string | number | null; className?: string; children: ReactNode }) {
  const canView = usePermission('RETAILER.VIEW');
  const role = useAuthStore((s) => s.user?.role);
  const linkable = id != null && id !== '' && canView && !CUSTOMER_ROLES.includes(role ?? '');
  if (!linkable) return <>{children}</>;
  return (
    <Link to={`/retailers/${id}`} className={cn(linkCls, className)} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

/**
 * Order number → its detail page (`/orders/:id`), but ONLY when the viewer can open it (holds
 * ORDER.VIEW and isn't a customer — that route denies customers). Otherwise renders the children as
 * plain text, so it degrades safely (e.g. a retailer viewing their own invoice).
 */
export function OrderLink({ id, className, children }: { id?: string | number | null; className?: string; children: ReactNode }) {
  const canView = usePermission('ORDER.VIEW');
  const role = useAuthStore((s) => s.user?.role);
  const linkable = id != null && id !== '' && canView && !CUSTOMER_ROLES.includes(role ?? '');
  if (!linkable) return <>{children}</>;
  return (
    <Link to={`/orders/${id}`} className={cn(linkCls, className)} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

/**
 * Return number → that return's record (`/returns/:id`, which opens the register with the row's
 * drawer open). Gated on RETURN.VIEW like the route itself, so it degrades to plain text for anyone
 * who could not open the target. Unlike a row click, this is a real URL: it can be middle-clicked,
 * copied and pasted into a message.
 */
export function ReturnLink({ id, className, children }: { id?: string | number | null; className?: string; children: ReactNode }) {
  const canView = usePermission('RETURN.VIEW');
  const linkable = id != null && id !== '' && canView;
  if (!linkable) return <>{children}</>;
  return (
    <Link to={`/returns/${id}`} className={cn(linkCls, className)} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

/**
 * Staff-user name (placed-by / sales person) → the staff directory (`/people?tab=users`). Gated on
 * USER.VIEW, and NEVER linked for a SUPER_ADMIN — that's a system account with no meaningful person
 * page (per the request: "not clickable if it is super admin"). Degrades to plain text otherwise.
 * (There is no per-user detail page yet, so the target is the manageable staff list.)
 */
/**
 * Staff-member name → their detail page (`/staff/:id`), the read-first counterpart to `/retailers/:id`:
 * profile, role/reporting, account and territory book, with every field editable IN PLACE by a Super
 * Admin (staff fields are Super-Admin-only — see the backend's `user-field-policy.ts`). An
 * earlier version deep-linked to `/people?tab=users&userId=` and the list POPPED THE EDIT MODAL, so
 * merely reading who a payout went to dumped you in a form. Without an id we can only reach the bare
 * list, which is why passing one matters.
 */
export function UserLink({ id, role, className, children }: { id?: string | number | null; role?: string | null; className?: string; children: ReactNode }) {
  const canView = usePermission('USER.VIEW');
  const linkable = canView && role !== 'SUPER_ADMIN';
  if (!linkable) return <>{children}</>;
  const to = id != null && id !== '' ? `/staff/${id}` : '/people?tab=users';
  return (
    <Link to={to} className={cn(linkCls, className)} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

/**
 * WHOEVER DID SOMETHING → the page that is actually about them.
 *
 * A dealer's own login and a staff member's login are both rows in `users`, so linking every actor
 * as a "user" sent both to the staff directory — and a dealer has no staff page, so clicking
 * "Demo Retailer · Retailer" on an order landed on a list they are not in. This routes by the role
 * the record already carries: a customer login opens the customer's own record, everyone else opens
 * their staff page. Pass the ids you have; each branch degrades to plain text without one.
 *
 * (`UserLink` on its own is still right where the actor can only ever be staff.)
 */
export function PersonLink({ id, role, retailerId, farmerId, className, children }: {
  /** The `users` row id — `createdBy`, `salesPersonId`, `approvedBy`. */
  id?: string | number | null;
  role?: string | null;
  /** The record's retailer, for a RETAILER login: that IS the page about them. */
  retailerId?: string | number | null;
  farmerId?: string | number | null;
  className?: string;
  children: ReactNode;
}) {
  const r = String(role ?? '').toUpperCase();
  if (r === 'RETAILER') return <RetailerLink id={retailerId} className={className}>{children}</RetailerLink>;
  if (r === 'FARMER') return <FarmerLink id={farmerId} className={className}>{children}</FarmerLink>;
  return <UserLink id={id} role={role} className={className}>{children}</UserLink>;
}

/**
 * Product name → the catalogue, pre-filtered to that product (`/products?search=…`).
 *
 * There is no per-product detail route, so the honest destination is the list with the search
 * already applied — one click to the row instead of "go to Products, then type the name again".
 * Gated on PRODUCT.VIEW and denied to customers, like the others.
 */
export function ProductLink({ name, className, children }: { name?: string | null; className?: string; children: ReactNode }) {
  const canView = usePermission('PRODUCT.VIEW');
  const role = useAuthStore((s) => s.user?.role);
  const linkable = !!name && canView && !CUSTOMER_ROLES.includes(role ?? '');
  if (!linkable) return <>{children}</>;
  return (
    <Link to={`/products?search=${encodeURIComponent(name)}`} className={cn(linkCls, className)} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

/**
 * Farmer name → the farmers register with that row opened (`/people?tab=farmers&focus=…`).
 *
 * A farmer has no detail ROUTE of their own — their record opens as a modal on the register — so the
 * honest destination is the register with the row addressed, which `useFocusRow` there turns into
 * the open record. Same gating as the others: FARMER.VIEW, and never for a customer login, whose
 * own role is denied that route.
 */
export function FarmerLink({ id, className, children }: { id?: string | number | null; className?: string; children: ReactNode }) {
  const canView = usePermission('FARMER.VIEW');
  const role = useAuthStore((s) => s.user?.role);
  const linkable = id != null && id !== '' && canView && !CUSTOMER_ROLES.includes(role ?? '');
  if (!linkable) return <>{children}</>;
  return (
    <Link to={`/people?tab=farmers&focus=${id}`} className={cn(linkCls, className)} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}
